import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { Announcement, Clear, Send } from '@mui/icons-material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useGlobals } from 'src/shared/contexts/appContext';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { deleteEventParticipantList } from 'src/shared/services/eventParticipantService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { InvoiceStatusEnum } from 'src/shared/models/enums/invoiceStatusEnum';
import { InvoiceStatuses } from 'src/modules/invoice/components/invoiceStatuses';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { Link, useSearchParams } from 'react-router-dom';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { EventParticipant } from '../models/eventParticipant';
import { EventParticipantStatuses } from './eventParticipantStatuses';
import { EventParticipantSendEmailModal } from './eventParticipantSendEmailModal';
import { EventParticipantsDetailModal } from './eventParticipantsDetailModal';

type EventParticipantDataGridProps = {
  eventId: number;
  participants: EventParticipant[];
  gridColumns?: GridColDef[] | undefined;
  refetchData?: () => void;
};

export const EventParticipantDataGrid = (props: EventParticipantDataGridProps) => {
  const { eventId, participants, gridColumns = undefined, refetchData } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { setDateStamp } = useGlobals();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<number[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<EventParticipant[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedParticipantId = searchParams.get('participant');
  const selectedItem = selectedParticipantId ? participants.find((p) => p.id === Number(selectedParticipantId)) : undefined;
  const showDetailsModal = Boolean(selectedItem);

  const openParticipantModal = (id: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('participant', id.toString());
      return next;
    });
  };

  const closeParticipantModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('participant');
      next.delete('createInvoice');
      return next;
    });
  };

  // const [allRows, setAllRows] = useState<Member[]>([]); // Spar undan en array med data för alla sidor som hämtats för att kunna filtrera selected mot denna.
  const [data, setData] = useState<DataGridModel>({
    loading: false, // Change when actual data
    totalRows: participants.length,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    updateData('totalRows', participants.length);
  }, [participants]);

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('event:tableHeaders.memberNr'),
      sortable: true,
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return <Link to={`${RoutesEnum.MemberDetails}/${params.row.member_id}`}>{params.row.memberNr || ''}</Link>;
      },
    },
    {
      field: 'firstName',
      headerName: t('event:tableHeaders.firstName'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName || ''}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('event:tableHeaders.lastName'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName || ''}</>;
      },
    },
    {
      field: 'email',
      headerName: t('event:tableHeaders.email'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return (
          <Tooltip title={params.row.email || ''} arrow>
            <span>{params.row.email || ''}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'phone',
      headerName: t('event:tableHeaders.phone'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return (
          <Tooltip title={params.row.phone || ''} arrow>
            <span>{params.row.phone || ''}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: t('event:tableHeaders.status'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <EventParticipantStatuses status={params.row.status} chipSize='small' />;
      },
    },
    {
      field: 'createdAt',
      headerName: t('event:tableHeaders.date'),
      sortable: true,
      flex: 0.8,
      minWidth: 140,
      renderCell: (params: GridCellParams) => {
        return <span style={{ whiteSpace: 'nowrap' }}>{DateHelper.getDateTime(params.row.createdAt) || ''}</span>;
      },
    },
    {
      field: 'invoice',
      headerName: t('event:tableHeaders.invoice'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return params.row.invoiceOcr !== null ? (
          <Tooltip title={t('event:goToInvoice')} arrow disableInteractive>
            <Link to={`${RoutesEnum.InvoicesDetail}/${params.row.invoiceId}`}>{params.row.invoiceOcr}</Link>
          </Tooltip>
        ) : (
          ''
        );
      },
    },
    {
      field: 'amount',
      headerName: t('event:tableHeaders.amount'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return params.row.invoiceAmount !== null ? <AmountWithCurrency amount={params.row.invoiceAmount} /> : '';
      },
    },
    {
      field: 'paymentStatus',
      headerName: t('event:tableHeaders.status'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const { invoiceStatus } = params.row;
        return invoiceStatus ? <InvoiceStatuses status={invoiceStatus} chipSize='small' /> : <></>;
      },
    },
    {
      field: 'allergy',
      headerName: t('event:tableHeaders.allergy'),
      sortable: true,
      flex: 0.3,
      renderCell: (params: GridCellParams) => {
        if (!params.row.allergy) return null;
        return (
          <Tooltip title={params.row.allergy} arrow>
            <Announcement fontSize='small' color='warning' />
          </Tooltip>
        );
      },
    },
  ];

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await deleteEventParticipantList(selectedParticipantIds);
      if (isMounted()) toast.success(<>{t('event:deleteParticipantSuccess')}</>);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setShowConfirmDeleteDialog(false);
        setDateStamp(new Date());
      }
    }
  };

  const onRowClick = (params) => {
    openParticipantModal(params.row.id);
  };

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-cell': {
            cursor: 'pointer',
          },
        }}
      >
        <FormLoaderWrapper loading={loading}>
          <DataGrid<EventParticipant>
            autoHeight
            columns={gridColumns ?? columns}
            rows={participants}
            mode='client'
            disableSelectionOnClick
            checkboxSelection
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
            }}
            onRowClick={onRowClick}
            sortModel={sortModel}
            selectionModel={selectionModel}
            dataModel={data}
            onSortModelChange={(newSortModel) => {
              if (newSortModel.length) {
                setSortModel(newSortModel);
              } else {
                setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
              }
            }}
            onPageChange={(val) => setData((prev) => ({ ...prev, page: val }))}
            onPageSizeChange={(val) => setData((prev) => ({ ...prev, pageSize: val }))}
          />
        </FormLoaderWrapper>
      </Box>
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }}>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button
              variant='contained'
              disableElevation
              color='error'
              sx={{ mr: 1 }}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const selection = participants.filter((row) => selectedIDs.has(row.id || 0)).map((x) => x.id);
                setSelectedParticipantIds(selection);
                setShowConfirmDeleteDialog(true);
              }}
            >
              {t('common:button.delete')}
            </Button>
            <Button
              variant='contained'
              disableElevation
              endIcon={<Send fontSize='small' />}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const selection = participants.filter((row) => selectedIDs.has(row.id || 0));
                setSelectedParticipants(selection);
                setShowSendEmailModal(true);
              }}
              sx={{ mr: 1 }}
            >
              {t('member:footer.sendMessage')}
            </Button>
          </Box>
        </FixedFooter>
      )}
      {showConfirmDeleteDialog &&
        (() => {
          const selectedIDs = new Set(selectedParticipantIds);
          const selected = participants.filter((p) => selectedIDs.has(p.id));
          const hasInvoice = selected.some((p) => p.invoiceId);
          const hasPaidInvoice = selected.some((p) => p.invoiceStatus?.id === InvoiceStatusEnum.Paid);

          return (
            <Dialog open={showConfirmDeleteDialog}>
              <DialogTitle>{t('event:deleteParticipantConfirmTitle')}</DialogTitle>
              <DialogContent dividers>
                {hasPaidInvoice ? (
                  <Typography color='error'>{t('event:deleteParticipantPaidInvoiceBlocked')}</Typography>
                ) : (
                  <>
                    <Typography>{t('event:deleteParticipantConfirmDescription')}</Typography>
                    {hasInvoice && (
                      <Typography color='error' sx={{ mt: 1 }}>
                        {t('event:deleteParticipantInvoiceWarning')}
                      </Typography>
                    )}
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button autoFocus onClick={() => setShowConfirmDeleteDialog(false)}>
                  {t('common:button.cancel')}
                </Button>
                {!hasPaidInvoice && (
                  <Button onClick={() => handleDeleteConfirm()} disableElevation variant='contained'>
                    {t('common:button.ok')}
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          );
        })()}
      {showSendEmailModal && (
        <EventParticipantSendEmailModal
          modalOpen={showSendEmailModal}
          eventId={eventId}
          selectedEventParticipants={selectedParticipants}
          onSave={() => setShowSendEmailModal(false)}
          onClose={() => setShowSendEmailModal(false)}
        />
      )}
      {showDetailsModal && selectedItem && (
        <EventParticipantsDetailModal
          modalOpen={showDetailsModal}
          participant={selectedItem}
          eventId={eventId}
          onClose={closeParticipantModal}
          refetchData={refetchData}
          onOpenParticipant={(pid) => {
            const p = participants.find((x) => x.id === pid);
            if (p) {
              openParticipantModal(pid);
            }
          }}
        />
      )}
    </>
  );
};
